<template>
  <div class="vue-import-tab">
    <h3 class="table-name">Autoprovisioning Imports</h3>
    
    <div class="form-group">
      <input type="text" class="form-control" v-model="searchQuery" placeholder="Search">
    </div>
    
    <div class="filter-buttons">
      <button 
        :class="['btn', 'btn-sm', statusFilter === 'all' ? 'btn-primary' : 'btn-default']"
        @click="statusFilter = 'all'">
        All
      </button>
      <button 
        :class="['btn', 'btn-sm', statusFilter === 'completed' ? 'btn-primary' : 'btn-default']"
        @click="statusFilter = 'completed'">
        Completed
      </button>
      <button 
        :class="['btn', 'btn-sm', statusFilter === 'incomplete' ? 'btn-primary' : 'btn-default']"
        @click="statusFilter = 'incomplete'">
        Incomplete
      </button>
    </div>
    
    <div v-if="loading" class="text-center py-3">
      <i class="fa fa-spinner fa-spin"></i> Loading autoprovisioning import data...
    </div>
    
    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>
    
    <div v-else>
      <div class="table-responsive">
        <table class="table table-striped table-hover autoprovisioning-table">
          <thead>
            <tr>
              <th @click="sort('name')">
                Domain Name
                <span v-if="sortKey === 'name'">
                  <i :class="sortReverse ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"></i>
                </span>
              </th>
              <th @click="sort('rosterService')">
                Service
                <span v-if="sortKey === 'rosterService'">
                  <i :class="sortReverse ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"></i>
                </span>
              </th>
              <th @click="sort('lastCompletedImportDate')">
                Last Completed Import
                <span v-if="sortKey === 'lastCompletedImportDate'">
                  <i :class="sortReverse ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"></i>
                </span>
              </th>
              <th @click="sort('lastImportDate')">
                Latest Import Status
                <span v-if="sortKey === 'lastImportDate'">
                  <i :class="sortReverse ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"></i>
                </span>
              </th>
              <th @click="sort('latestFileLastModified')">
                Latest File
                <span v-if="sortKey === 'latestFileLastModified'">
                  <i :class="sortReverse ? 'fa fa-sort-asc' : 'fa fa-sort-desc'"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="domain in sortedDomains">
              <tr :key="domain.uuid">
                <td><a :href="`#!/domains/${domain.uuid}`" class="view-link">{{ domain.name }}</a></td>
                <td>
                  <span class="service-badge">{{ domain.rosterService }}</span>
                </td>
                <td>
                  <div v-if="domain.lastCompletedImport" class="import-status-cell">
                    <a :href="getImportLink({ uuid: domain.lastCompletedImport.uuid, status: domain.lastCompletedImport.status, domain_uuid: domain.uuid })">
                      {{ domain.lastCompletedImport.status }}
                    </a>
                    <div class="timestamp">{{ formatDate(domain.lastCompletedImport.created_at) }}</div>
                  </div>
                  <span v-else>-</span>
                </td>
                <td>
                  <div v-if="domain.lastImportUuid" class="import-status-cell">
                    <div>
                      <a :href="getImportLink({ uuid: domain.lastImportUuid, status: domain.lastImportStatus, domain_uuid: domain.uuid })">
                        {{ domain.lastImportStatus }}
                      </a>
                      <tooltip text="Preprocessing guardrails detected for this import. Click to view details.">
                        <button
                          type="button"
                          v-if="domain.guardrails && domain.guardrails.length > 0"
                          :aria-expanded="isGuardrailsExpanded(domain.uuid)"
                          :aria-controls="isGuardrailsExpanded(domain.uuid) ? `guardrail-details-${domain.uuid}` : null"
                          :aria-label="`${isGuardrailsExpanded(domain.uuid) ? 'Hide' : 'Show'} ${domain.guardrails.length} preprocessing guardrails for ${domain.name}`"
                          class="guardrail-indicator"
                          @click="toggleRow(domain.uuid, 'guardrails')">
                          <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                          {{ domain.guardrails.length }}
                        </button>
                      </tooltip>
                    </div>
                    <div class="timestamp">{{ formatDate(domain.lastImportDate) }}</div>
                  </div>
                  <span v-else>-</span>
                </td>
                <td>
                <div v-if="domain.latestFileName" class="latest-file-cell">
                  <div>
                    <code>{{ domain.latestFileName }}</code>
                    <file-download-button
                      v-if="domain.latestFileDownloadUrl"
                      :url="domain.latestFileDownloadUrl"
                      :file-name="domain.latestFileName"
                    />
                  </div>
                  <div v-if="domain.latestFileLastModified" class="timestamp-row">
                    <span class="timestamp-label">Last Modified: </span>
                    <span class="timestamp">{{ formatDate(domain.latestFileLastModified) }}</span>
                    <stale-file-icon v-if="isStaleFile(domain.latestFileLastModified)" />
                  </div>
                </div>
                <span v-else>-</span>
              </td>
              </tr>
              <tr
                v-if="domain.guardrails && domain.guardrails.length > 0 && isGuardrailsExpanded(domain.uuid)"
                :id="`guardrail-details-${domain.uuid}`"
                :key="`${domain.uuid}-guardrail-details`"
                class="guardrail-details-row">
                <td colspan="5">
                  <guardrail-details :guardrails="domain.guardrails" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip } from 'uiv'
import GuardrailDetails from './GuardrailDetails.vue'
import FileDownloadButton from '../../domains/autoprovisioning/FileDownloadButton.vue'
import { formatDate, isOlderThanDays } from '../../utils/formatters'
import StaleFileIcon from '../../domains/autoprovisioning/StaleFileIcon.vue'

const IMPORT_STATUS_COMPLETED = 'completed'
const IMPORT_STATUS_COMPLETED_WITH_ERRORS = 'completed with errors'

export default {
  name: 'AutoprovisioningDashboard',
  components: {
    FileDownloadButton,
    GuardrailDetails,
    StaleFileIcon,
    Tooltip
  },
  inject: ['adminApi', 'angularScope'],
  data() {
    return {
      loading: false,
      error: null,
      domains: [],
      searchQuery: '',
      sortKey: '',
      sortReverse: false,
      statusFilter: 'all', // 'all', 'completed', 'incomplete'
      rosterServiceDisplayNames: {
        's3csv': 'SFTP'
      },
      expandedRows: {} // { domainUuid: detailType }
    }
  },
  computed: {
    isStaleFile() {
      return (lastModified) => isOlderThanDays(lastModified)
    },
    isGuardrailsExpanded() {
      return (domainUuid) => this.expandedRows[domainUuid] === 'guardrails'
    },
    filteredDomains() {
      let domains = this.domains
      
      // Apply status filter
      if (this.statusFilter === 'completed') {
        domains = domains.filter(domain => this.isImportCompleted(domain.lastImportStatus))
      } else if (this.statusFilter === 'incomplete') {
        domains = domains.filter(domain => !this.isImportCompleted(domain.lastImportStatus))
      }
      
      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        domains = domains.filter(domain => {
          return (domain.name && domain.name.toLowerCase().includes(query)) ||
                 (domain.lastImportStatus && domain.lastImportStatus.toLowerCase().includes(query)) ||
                 (domain.lastCompletedImport && domain.lastCompletedImport.status && domain.lastCompletedImport.status.toLowerCase().includes(query))
        })
      }
      
      return domains
    },
    sortedDomains() {
      const domains = [...this.filteredDomains]
      // Default to sorting by lastImportDate
      const sortKey = this.sortKey || 'lastImportDate'
      const sortReverse = this.sortKey ? this.sortReverse : true
      
      return domains.sort((a, b) => {
        let aVal = a[sortKey]
        let bVal = b[sortKey]
        
        // Handle null values - push them to the end
        if (aVal === null || aVal === undefined) return 1
        if (bVal === null || bVal === undefined) return -1
        
        // Case-insensitive string comparison
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase()
          bVal = bVal ? bVal.toLowerCase() : ''
        }
        
        // Primary sort: compare values in the selected direction
        if (aVal < bVal) return sortReverse ? 1 : -1
        if (aVal > bVal) return sortReverse ? -1 : 1
        
        // Secondary sort: when primary values are equal, sort by name alphabetically
        const aName = a.name ? a.name.toLowerCase() : ''
        const bName = b.name ? b.name.toLowerCase() : ''
        if (aName < bName) return -1
        if (aName > bName) return 1
        return 0
      })
    }
  },
  mounted() {
    this.unsubscribe = this.angularScope.$on('autoprovisioningTabSelected', () => {
      this.loadData()
    })
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
    }
  },
  methods: {
    async loadData() {
      if (this.loading || this.domains.length > 0) return
      
      this.loading = true
      this.error = null
      try {
        const importData = await this.adminApi.getAutoprovisioningImportData()
        
        this.domains = importData.map(data => ({
          uuid: data.latest_import?.domain?.uuid || data.domain_uuid,
          name: data.latest_import?.domain?.name || data.domain_name,
          rosterService: this.rosterServiceDisplayNames[data.roster_service] || data.roster_service,
          lastImportStatus: data.latest_import?.status || null,
          lastImportDate: data.latest_import?.created_at || null,
          lastImportUuid: data.latest_import?.uuid || null,
          lastCompletedImport: data.latest_completed_import || null,
          lastCompletedImportDate: data.latest_completed_import?.created_at || null,
          latestFileName: data.latest_sftp_file?.file_name || null,
          latestFileLastModified: data.latest_sftp_file?.last_modified || null,
          latestFileDownloadUrl: data.latest_sftp_file?.download_url || null,
          guardrails: data.latest_import?.preprocessing_guardrails || []
          }
        ))
      } catch (error) {
        console.error('Error loading autoprovisioning data:', error)
        this.error = 'Failed to load autoprovisioning data. Please try again.'
      } finally {
        this.loading = false
      }
    },
    sort(key) {
      if (this.sortKey === key) {
        this.sortReverse = !this.sortReverse
      } else {
        this.sortKey = key
        this.sortReverse = false
      }
    },
    isImportCompleted(status) {
      return status === IMPORT_STATUS_COMPLETED || status === IMPORT_STATUS_COMPLETED_WITH_ERRORS
    },
    getImportLink(importRecord) {
      const step = this.isImportCompleted(importRecord.status) ? 'confirm' : 'status'
      return `#!/import_wizard/${importRecord.domain_uuid}/${importRecord.uuid}/${step}`
    },
    formatDate,
    toggleRow(uuid, detailType) {
      if (this.expandedRows[uuid] === detailType) {
        // Collapse if clicking the same type
        this.$delete(this.expandedRows, uuid)
      } else {
        // Expand with new type (replaces any existing expanded type)
        this.$set(this.expandedRows, uuid, detailType)
      }
    },
  }
}
</script>

<style scoped>
@import '../../styles/foundations.css';

.table-name{
  font-size: 30px;
}

.filter-buttons {
  margin-bottom: 15px;
}

.filter-buttons .btn {
  margin-right: 5px;
}

.autoprovisioning-table th {
  border-bottom: 2px solid var(--gray-100);
  font-weight: 600;
  color: var(--gray-500);
  font-size: 14px;
  cursor: pointer;
}

.autoprovisioning-table td {
  vertical-align: middle;
  padding: 12px 8px;
}

.import-status-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.latest-file-cell {
  display: flex;
  flex-direction: column;
}

.latest-file-cell .timestamp-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--gray-400);
}

.timestamp {
  font-size: 12px;
  color: var(--gray-400);
}

.service-badge {
  background-color: var(--gray-100);
  color: var(--gray-500);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.guardrail-indicator {
  background-color: var(--amber-100);
  width: fit-content;
  color: var(--gray-500);
  padding: 0px 4px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid var(--amber-200);
}

.guardrail-indicator:hover {
  background-color: var(--amber-200);
  border-color: var(--amber-300);
}

.guardrail-indicator i {
  font-size: 11px;
  padding-right: 2px;
}

.guardrail-details-row {
  background-color: var(--gray-50);
}

.timestamp-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

</style>
